import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";

const NearFarGame: React.FC = () => {
    const emojis = [
        "🐶", "🚗", "🦄", "🎈", "🚀", "🐘", "🍎", "🏀", "📕", "⚽",
        "🍕", "🎸", "🚢", "🐱", "🌞", "🍔", "🚲", "🏕", "📡", "🔑",
        "🐳", "🌵", "🎠", "🍩", "🧀", "🚂", "🐙", "🦆", "🏖", "📖",
        "🍉", "🌊", "🎻", "🌈", "🍟", "⚡", "🎯", "🐟", "🚜", "🦅",
        "📱", "📚", "🥕", "🦖", "🎃", "🐬", "🎁", "🍇", "🚀", "🛶",
        "🦜", "🏹", "🐧", "🦊", "🏰", "🚦", "🎠", "🌼", "💎", "🗻",
        "🦄", "📌", "🥦", "🎷", "🔬", "🐼", "📎", "🦕", "🍵", "⚔️",
        "🦉", "🌙", "🎭", "🏜", "📀", "🦢", "🐎", "🧊", "🎇", "🏄",
        "🚁", "🦋", "🦔", "🍪", "🛰", "🐝", "🎤", "🦑", "🏎", "🍍"
    ];

    const gridSize = 5; // 5x5 Grid
    const [grid, setGrid] = useState<string[][]>([]);
    const [nearEmoji, setNearEmoji] = useState<string>("");
    const [farEmoji, setFarEmoji] = useState<string>("");
    const [questionType, setQuestionType] = useState<"near" | "far">("near");
    const [message, setMessage] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateQuestion();
        document.title = "Near and Far – LKG Pre-Math Worksheet | Mama’s Guidance"; // Set page title
    }, []);

    const generateQuestion = () => {
        const emoji1 = emojis[Math.floor(Math.random() * emojis.length)];
        let emoji2;
        do {
            emoji2 = emojis[Math.floor(Math.random() * emojis.length)];
        } while (emoji1 === emoji2);

        setNearEmoji(emoji1);
        setFarEmoji(emoji2);
        setMessage("");

        // Randomly decide if the question is "near" or "far"
        setQuestionType(Math.random() < 0.5 ? "near" : "far");

        // Create empty grid
        const newGrid = Array(gridSize)
            .fill(null)
            .map(() => Array(gridSize).fill(""));

        const houseRow = Math.floor(gridSize / 2);
        const houseCol = Math.floor(gridSize / 2);
        newGrid[houseRow][houseCol] = "🏠";

        // Get valid near positions (adjacent to house)
        const nearPositions = [
            [houseRow - 1, houseCol],
            [houseRow + 1, houseCol],
            [houseRow, houseCol - 1],
            [houseRow, houseCol + 1]
        ].filter(([r, c]) => r >= 0 && r < gridSize && c >= 0 && c < gridSize);

        // Pick a random near position
        const [nearRow, nearCol] = nearPositions[Math.floor(Math.random() * nearPositions.length)];
        newGrid[nearRow][nearCol] = emoji1;

        // Get valid far positions (not adjacent to house)
        const farPositions = [];
        for (let r = 0; r < gridSize; r++) {
            for (let c = 0; c < gridSize; c++) {
                if (newGrid[r][c] === "" && Math.abs(r - houseRow) + Math.abs(c - houseCol) > 2) {
                    farPositions.push([r, c]);
                }
            }
        }

        const [farRow, farCol] = farPositions[Math.floor(Math.random() * farPositions.length)];
        newGrid[farRow][farCol] = emoji2;

        setGrid(newGrid);
    };

    const checkAnswer = (selected: string) => {
        if ((questionType === "near" && selected === nearEmoji) ||
            (questionType === "far" && selected === farEmoji)) {
            setShowImage(true);
            setTimeout(() => {
                generateQuestion()
                setShowImage(false);
                setMessage("")
            }, 5000)
        } else {
            setShowImage(false);
            setMessage("❌ Oops! Try again.");
        }
    };

    return (

        <>
            <Helmet>
                <meta name="description" content='Introduce the concept of near and far to LKG children with fun, interactive activities on Mama’s Guidance—designed to develop spatial awareness and early observation skills through engaging visual learning.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord pickColor matchImage match-container game-container">
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Near and Far</h1>

                                    <p>
                                        Spatial awareness starts with understanding the position of objects — and the concept of <strong>near and far</strong> is the perfect place to begin! This engaging worksheet is specially created for LKG children to help them identify and compare objects based on their distance.
                                    </p>

                                    <h2>🧭 What Does Near and Far Mean?</h2>
                                    <p>
                                        “<strong>Near</strong>” refers to something that is close to us, while “<strong>Far</strong>” refers to something that is at a distance. Learning this helps children describe the location of objects in space — a key early math and communication skill.
                                    </p>

                                    <h2>🧩 What’s in the Worksheet?</h2>
                                    <ul>
                                        <li>Illustrations showing one object near and another far (<strong>e.g., a tree near a house vs. far from it</strong>)</li>
                                        <li>Circle the object that is near or far</li>
                                        <li>Draw lines to match near/far pairs</li>
                                        <li>Coloring activities with fun spatial themes</li>
                                    </ul>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Understand spatial concepts like <strong>distance and position</strong></li>
                                        <li>Identify <strong>near and far</strong> visually</li>
                                        <li>Develop observation and reasoning skills</li>
                                        <li>Improve <strong>vocabulary</strong> (near, far, closer, farther)</li>
                                    </ul>

                                    <h2>👩‍🏫 How to Use the Worksheet</h2>
                                    <ul>
                                        <li>Show your child pictures and ask: <strong>“Which is near?”</strong> and <strong>“Which is far?”</strong></li>
                                        <li>Let them circle or color the right objects.</li>
                                        <li>Use real objects like toys — <strong>place one near and one far from the child and ask them to identify</strong>.</li>
                                        <li>Play a game where they find something near them and something far from them.</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/lkg/mathematics/pre-math-concepts/long-short"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Long - Short</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/pre-math-concepts/big-small"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Big - Small</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/pre-math-concepts/full-empty"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Full - Empty</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🧠 Why This Concept Is Important</h2>
                                    <p>
                                        Understanding <strong>near and far</strong> improves <strong>spatial sense</strong>, which is crucial for navigating the environment, giving directions, and later geometry learning.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Near and Far</h1>

                                <p>
                                    Spatial awareness starts with understanding the position of objects — and the concept of <strong>near and far</strong> is the perfect place to begin! This engaging worksheet is specially created for LKG children to help them identify and compare objects based on their distance.
                                </p>

                                <h2>🧭 What Does Near and Far Mean?</h2>
                                <p>
                                    “<strong>Near</strong>” refers to something that is close to us, while “<strong>Far</strong>” refers to something that is at a distance. Learning this helps children describe the location of objects in space — a key early math and communication skill.
                                </p>

                                <h2>🧩 What’s in the Worksheet?</h2>
                                <ul>
                                    <li>Illustrations showing one object near and another far (e.g., a tree near a house vs. far from it)</li>
                                    <li>Circle the object that is near or far</li>
                                    <li>Draw lines to match near/far pairs</li>
                                    <li>Coloring activities with fun spatial themes</li>
                                </ul>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Understand spatial concepts like <strong>distance and position</strong></li>
                                    <li>Identify <strong>near and far</strong> visually</li>
                                    <li>Develop observation and reasoning skills</li>
                                    <li>Improve <strong>vocabulary</strong> (near, far, closer, farther)</li>
                                </ul>

                                <h2>👩‍🏫 How to Use the Worksheet</h2>
                                <ul>
                                    <li>Show your child pictures and ask: <strong>“Which is near?”</strong> and <strong>“Which is far?”</strong></li>
                                    <li>Let them circle or color the right objects.</li>
                                    <li>Use real objects like toys — place one near and one far from the child and ask them to identify.</li>
                                    <li>Play a game where they find something near them and something far from them.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/long-short"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Long - Short</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/big-small"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Big - Small</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/full-empty"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Full - Empty</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why This Concept Is Important</h2>
                                <p>
                                    Understanding <strong>near and far</strong> improves <strong>spatial sense</strong>, which is crucial for navigating the environment, giving directions, and later geometry learning.
                                </p>
                            </article>
                            <h2>
                                {questionType === "near"
                                    ? "Which object is NEAR the house? 🏠"
                                    : "Which object is FAR from the house? 🏠"}
                            </h2>
                            <div className="">
                                {grid.map((row, rowIndex) => (
                                    <div key={rowIndex} className="grid-row">
                                        {row.map((cell, colIndex) => (
                                            <div
                                                key={colIndex}
                                                className={`grid-cell ${cell === "" ? "disabled" : ""}`}
                                                onClick={() => cell !== "" && cell !== "🏠" && checkAnswer(cell)}
                                            >
                                                {cell}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                            <p>{message}</p>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default NearFarGame;
